import { memo, useMemo } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { pickTextureUrl, loadTextureMaps } from './textureUtils'
import { useAppStore } from '../../stores/appStore'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { ROTATE_QUARTER_TURNS_DEFAULT, SWAP_UV_DEFAULT, clearLegacyMapsOnMesh, unifyRepeatsFromBase, applyPBR, swapMeshUV, calculateUVAwareRepeat } from './textureUtils'

type Offsets = { x?: number; y?: number; z?: number }
type HeadboardSlotTextureOption = {
  slot_name: string
  slot_label: string
  textures: any[]
}
const loggedHeadboardDebugKeys = new Set<string>()
const normalizeSlotKey = (value: string): string => {
  return String(value || '')
    .trim()
    .toLowerCase()
    // Remove common DCC numeric suffixes like ".001" / "_001"
    .replace(/[._-]\d{3,}$/, '')
    // Normalize separators to underscore for consistent matching
    .replace(/[\s.-]+/g, '_')
}

function HeadboardCmp({ headboardUrl, texture, headboardTexture, headboardTextureOptions = [], onFloor = false, offsets = {} as Offsets }: { headboardUrl: string; texture: any; headboardTexture?: any; headboardTextureOptions?: HeadboardSlotTextureOption[]; onFloor?: boolean; offsets?: Offsets }) {
  const effectiveTexture = headboardTexture || texture
  const urlParams = new URLSearchParams(window.location.search)
  const debugHeadboard =
    urlParams.get('hbdebug') === '1' ||
    (window as any).__HB_DEBUG__ === true
  const hbSlotRaw = urlParams.get('hbslotraw') === '1'
  const hbSlotNoSwap = hbSlotRaw || urlParams.get('hbslotnoswap') === '1'
  const hbSlotNoRotate = hbSlotRaw || urlParams.get('hbslotnorotate') === '1'
  const hbSlotNoRepeat = hbSlotRaw || urlParams.get('hbslotnorepeat') === '1'
  
  const { bedConfig } = useAppStore()
  const selectedSize = bedConfig.bed.selectedSize // Bed width in cm
  
  const headboard = useLoader(FBXLoader, headboardUrl)
  const headboardBox = new THREE.Box3().setFromObject(headboard)
  const headboardCenter = headboardBox.getCenter(new THREE.Vector3())
  const headboardMin = headboardBox.min
  const BASE_SCALE = 0.05
  const headboardY = -headboardBox.min.y * BASE_SCALE

  const bedBox = (window as any).__bed_bbox__
  if (!bedBox || bedBox.isEmpty()) return null as any

  // Base placement: mounted vs floor (keep mounted default, keep floor closer)
  const headboardX = 0
  const baseZ = onFloor ? (bedBox.min.z - 0.25) : (bedBox.min.z - 0.15)
  const headboardZ = baseZ
  
  const legHeight = (window as any).__bed_lift_y__ || 0
  const hbSlug = (bedConfig.headboard?.slug || '').toLowerCase()
  const bedSlug = `${bedConfig.bed.variation?.slug || ''} ${bedConfig.bed.variation?.title || ''}`.toLowerCase()


  let finalY = onFloor ? headboardY : headboardY + legHeight

  // Apply special adjustment only if bedSlug does NOT contain "icon"
  if (
    hbSlug === 'rest-adjustable' &&
    ['majestic', 'supreme', 'j6', 'carat', 'prestige', 'j5'].some((k) => bedSlug.includes(k)) &&
    !bedSlug.includes('icon')
  ) {
    console.log({ finalY, legHeight })
    if ((bedConfig.legs as any)?.height === 14) {
      finalY = headboardY + (legHeight / 14)
    }

    if ((bedConfig.legs as any)?.height === 18) {
      finalY = headboardY + (legHeight / 3)
    }
  }


  console.log('finalY', finalY)
  const clone = headboard.clone()
  clone.scale.setScalar(BASE_SCALE)
  clone.position.set(-headboardCenter.x * BASE_SCALE, -headboardMin.y * BASE_SCALE, -headboardCenter.z * BASE_SCALE)

  if (debugHeadboard) {
    const debugKey = `${headboardUrl}|${texture?.id || 'no-bed-texture'}|${headboardTexture?.id || 'no-headboard-texture'}`
    if (!loggedHeadboardDebugKeys.has(debugKey)) {
      loggedHeadboardDebugKeys.add(debugKey)

      // Extract slot/material names from the source headboard model.
      const sourceSlots = new Set<string>()
      headboard.traverse((child: any) => {
        if (!child?.isMesh || !child?.material) return
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach((m: any) => {
          if (m?.name && m.name !== 'unnamed') sourceSlots.add(m.name)
        })
      })
      const sortedSourceSlots = Array.from(sourceSlots).sort()
      ;(window as any).__headboard_model_material_slots__ = sortedSourceSlots

      console.log('[HeadboardDebug] Render input', {
        headboardUrl,
        selectedSize,
        debugFlags: {
          hbSlotRaw,
          hbSlotNoSwap,
          hbSlotNoRotate,
          hbSlotNoRepeat,
        },
        usingHeadboardTextureOverride: !!headboardTexture,
        bedTexture: texture
          ? { id: texture.id, name: texture.name }
          : null,
        headboardTexture: headboardTexture
          ? { id: headboardTexture.id, name: headboardTexture.name }
          : null,
        effectiveTexture: effectiveTexture
          ? { id: effectiveTexture.id, name: effectiveTexture.name }
          : null,
        sourceSlotCount: sortedSourceSlots.length,
        sourceSlots: sortedSourceSlots,
      })
    }
  }

  const normalizedSlotTextureOptions = useMemo(() => {
    return (headboardTextureOptions || [])
      .filter((slot) => !!slot?.slot_name)
      .map((slot) => ({
        slot_name: String(slot.slot_name).toLowerCase(),
        slot_label: slot.slot_label,
        texture: slot?.textures?.[0] || null,
      }))
  }, [JSON.stringify((headboardTextureOptions || []).map((slot) => ({
    slot_name: slot?.slot_name,
    texture_id: slot?.textures?.[0]?.id || null,
  })))])
  const slotTextureByName = useMemo(() => {
    const byName: Record<string, any> = {}
    normalizedSlotTextureOptions.forEach((slot) => {
      if (slot.slot_name && slot.texture) {
        byName[normalizeSlotKey(slot.slot_name)] = slot.texture
      }
    })
    return byName
  }, [normalizedSlotTextureOptions])

  // Use useLoader for proper texture caching and quality, like BedModel does
  const maps = effectiveTexture?.base_color_url || (effectiveTexture as any)?.base_color_urls ? {
    base: (() => {const u = pickTextureUrl((effectiveTexture as any).base_color_urls, effectiveTexture.base_color_url); return u ? (useLoader(THREE.TextureLoader, u) as THREE.Texture) : null})(),
    normal: (() => {const u = pickTextureUrl((effectiveTexture as any).normal_urls, effectiveTexture.normal_url); return u ? (useLoader(THREE.TextureLoader, u) as THREE.Texture) : null})(),
    roughness: (() => {const u = pickTextureUrl((effectiveTexture as any).roughness_urls, effectiveTexture.roughness_url); return u ? (useLoader(THREE.TextureLoader, u) as THREE.Texture) : null})(),
    metalness: (() => {const u = pickTextureUrl((effectiveTexture as any).metalness_urls, effectiveTexture.metalness_url); return u ? (useLoader(THREE.TextureLoader, u) as THREE.Texture) : null})(),
    ao: (() => {const u = pickTextureUrl((effectiveTexture as any).ambient_occlusion_urls, effectiveTexture.ambient_occlusion_url); return u ? (useLoader(THREE.TextureLoader, u) as THREE.Texture) : null})(),
    displacement: (() => {const u = pickTextureUrl((effectiveTexture as any)?.height_urls, (effectiveTexture as any)?.height_url); return u ? (useLoader(THREE.TextureLoader, u) as THREE.Texture) : null})(),
  } : null

  if (maps) {
    if (debugHeadboard) {
      console.log('[HeadboardDebug] Loaded texture maps', {
        textureId: effectiveTexture?.id || null,
        textureName: effectiveTexture?.name || null,
        hasSlotSpecificTextures: Object.keys(slotTextureByName).length > 0,
        maps: {
          base: !!maps.base,
          normal: !!maps.normal,
          roughness: !!maps.roughness,
          metalness: !!maps.metalness,
          ao: !!maps.ao,
          displacement: !!maps.displacement,
        },
      })
    }

    clone.traverse((child: any) => {
      if (child.isMesh && child.material) {
        const meshMaterials = Array.isArray(child.material) ? child.material : [child.material]
        const meshHasSlotMappedMaterial = meshMaterials.some((m: any) => {
          const name = normalizeSlotKey(String(m?.name || ''))
          if (slotTextureByName[name]) return true
          // Fuzzy fallback: allow prefix matches either direction.
          const keys = Object.keys(slotTextureByName)
          return keys.some((k) => name.startsWith(k) || k.startsWith(name))
        })

        // Isolate UV mutations per clone instance (same safety pattern as tables).
        try {
          child.geometry = (child.geometry as THREE.BufferGeometry).clone()
          if (child.geometry && child.geometry.userData) {
            Object.keys(child.geometry.userData)
              .filter((k) => k.startsWith('_origUV_'))
              .forEach((k) => delete child.geometry.userData[k])
          }
        } catch {}

        clearLegacyMapsOnMesh(child)
        // Slot-mapped materials (e.g. Link_Oak) keep authored UV orientation.
        // Fallback textile materials keep the old swapped UV behavior.
        const shouldSwapUv = SWAP_UV_DEFAULT && !hbSlotNoSwap && !meshHasSlotMappedMaterial
        swapMeshUV(child, shouldSwapUv, 'headboard')
        let origMats = child.material
        let isArray = Array.isArray(origMats)
        let materials = isArray ? origMats : [origMats]

        let newMaterials = materials.map((origMat: any, index: number) => {
          // Always use fresh MeshStandardMaterial
          const mat: THREE.MeshStandardMaterial = new THREE.MeshStandardMaterial()
          mat.color = new THREE.Color(0xffffff)
          ;(mat as any).emissive?.setRGB?.(0, 0, 0)
          ;(mat as any).vertexColors = false
          
          // Calculate UV-aware repeat based on headboard dimensions
          // Use headboard's own dimensions and UV data, independent of bed
          const headboardSize = new THREE.Vector3().subVectors(headboardBox.max, headboardBox.min)
          // Width: use bed width (selectedSize in cm), convert to meters
          const headboardWidthM = selectedSize ? selectedSize / 100 : (headboardSize.x / 100)
          // Height: use headboard's height in meters (headboardBox is unscaled, so divide by 100 to get meters)
          const headboardHeightM = headboardSize.y / 100
          
          // Calculate repeat values accounting for UV scaling
          const repeat = calculateUVAwareRepeat(child, headboardWidthM || 1.8, headboardHeightM || 1.0, false, true) // Normalize UV span for consistency
          
          const originalMatNameRaw = String(origMat?.name || '')
          const originalMatName = normalizeSlotKey(originalMatNameRaw)
          const slotTexture =
            slotTextureByName[originalMatName] ||
            (() => {
              const keys = Object.keys(slotTextureByName)
              const key = keys.find((k) => originalMatName.startsWith(k) || k.startsWith(originalMatName))
              return key ? slotTextureByName[key] : null
            })()
          const loader = new THREE.TextureLoader()
          const slotMaps = slotTexture ? loadTextureMaps(slotTexture, loader) : null
          const mapsToApply = slotMaps || maps
          const isSlotMappedMaterial = !!slotMaps
          // Raw UV transform path for slot-mapped materials.
          const slotRotateTurns = (isSlotMappedMaterial || hbSlotNoRotate) ? 0 : ROTATE_QUARTER_TURNS_DEFAULT

          if (debugHeadboard) {
            console.log('[HeadboardDebug] Slot match', {
              materialName: originalMatNameRaw || null,
              normalizedMaterialName: originalMatName,
              matchedSlot: slotMaps ? originalMatName : null,
              matchedTextureId: slotTexture?.id || null,
              matchedTextureName: slotTexture?.name || null,
              fallbackTextureId: effectiveTexture?.id || null,
              fallbackTextureName: effectiveTexture?.name || null,
              uvMode: isSlotMappedMaterial ? 'accessory-like' : 'textile-pipeline',
              slotRotateTurns,
              shouldSwapUv,
              meshHasSlotMappedMaterial,
            })
          }

          applyPBR({
            mat,
            origMat,
            type: isSlotMappedMaterial ? 'wood' : 'textile',
            meshName: child.name,
            maps: mapsToApply,
            rotateTurns: slotRotateTurns,
            hasUV2: !!(child.geometry as any).attributes.uv2,
            // Slot-mapped materials (e.g. Link_Oak) should behave like tables:
            // keep authored UV scaling (no textile repeat normalization).
            repeat: isSlotMappedMaterial
              ? { x: 1, y: 1 }
              : repeat,
            offset: { x: 0, y: 0 },
            rotation: 0,
          })

          // Align post-apply flags with bed
          mat.transparent = false
          mat.opacity = 1.0
          mat.depthTest = true
          mat.depthWrite = true
          mat.name = `${child.name}_Material_${index}`
          mat.needsUpdate = true
          // Intentionally no extra console logging here in production
          return mat
        })

        child.material = isArray ? newMaterials : newMaterials[0]
        const matsToUnify = Array.isArray(child.material) ? child.material : [child.material]
        matsToUnify.forEach(unifyRepeatsFromBase)
      }
    })
  }

  clone.traverse((child: any) => {
    if (child.isMesh) {
      child.castShadow = true
      child.receiveShadow = true
    }
  })

  // Apply optional unitless offsets
  const ox = Number(offsets.x ?? 0)
  const oy = Number(offsets.y ?? 0)
  const oz = Number(offsets.z ?? 0)

  return (
    <group position={[headboardX + ox, finalY + oy, headboardZ + oz]}>
      <primitive object={clone} />
    </group>
  )
}

const Headboard = memo(HeadboardCmp, (prev, next) => {
  const prevTex = prev.texture
  const nextTex = next.texture
  const prevHeadTex = prev.headboardTexture
  const nextHeadTex = next.headboardTexture
  const prevSlotSig = JSON.stringify((prev.headboardTextureOptions || []).map((slot: any) => ({
    slot_name: slot?.slot_name,
    texture_id: slot?.textures?.[0]?.id || null,
  })))
  const nextSlotSig = JSON.stringify((next.headboardTextureOptions || []).map((slot: any) => ({
    slot_name: slot?.slot_name,
    texture_id: slot?.textures?.[0]?.id || null,
  })))
  const prevKey = prevTex ? `${prevTex.id || ''}|${prevTex.base_color_url || ''}|${prevTex.normal_url || ''}|${prevTex.roughness_url || ''}|${prevTex.metalness_url || ''}|${prevTex.ambient_occlusion_url || ''}` : ''
  const nextKey = nextTex ? `${nextTex.id || ''}|${nextTex.base_color_url || ''}|${nextTex.normal_url || ''}|${nextTex.roughness_url || ''}|${nextTex.metalness_url || ''}|${nextTex.ambient_occlusion_url || ''}` : ''
  const prevHeadKey = prevHeadTex ? `${prevHeadTex.id || ''}|${prevHeadTex.base_color_url || ''}|${prevHeadTex.normal_url || ''}|${prevHeadTex.roughness_url || ''}|${prevHeadTex.metalness_url || ''}|${prevHeadTex.ambient_occlusion_url || ''}` : ''
  const nextHeadKey = nextHeadTex ? `${nextHeadTex.id || ''}|${nextHeadTex.base_color_url || ''}|${nextHeadTex.normal_url || ''}|${nextHeadTex.roughness_url || ''}|${nextHeadTex.metalness_url || ''}|${nextHeadTex.ambient_occlusion_url || ''}` : ''
  return prev.headboardUrl === next.headboardUrl && prevKey === nextKey && prevHeadKey === nextHeadKey && prevSlotSig === nextSlotSig
})
export default Headboard


